import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { FaCheckCircle, FaWhatsapp, FaFileDownload } from "react-icons/fa";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../services/firebaseConfig";
import { Helmet } from "react-helmet";


<Helmet>
  <title>Insurance Purchase Confirmed | Thank You | Kora Insurance</title>
  <meta name="description" content="Your insurance purchase is complete. A confirmation has been sent to your email and SMS." />
  <meta name="keywords" content="Insurance Ghana, Family Insurance, Affordable Cover, Kora Insurance" />
  <meta name="robots" content="noindex, nofollow" />
  <link ref="canonical" href="https://korame.com.gh" />
  {/* Open Graph tags */}
  <meta property="og:title" content="Best Online Insurance Platform" />
  <meta property="og:description" content="Compare and buy insurance plans with ease." />
  <meta property="og:url" content="https://korame.com.gh/" />
  <meta property="og:type" content="website" />
</Helmet>

const ConfirmationPage = ({
  whatsappNumber = "+233500490065",
  contactPhone = "+233500490065",
  support_email= "hello@summerhealth.io",
}) => {
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Destructure with enhanced validation
  const {
    fullName = "",
    coverOption = "",
    tier = "",
    annualAmount = 0,
    phoneNumber = "",
    email = "",
    policyNumber = "",
    dateOfBirth = "",
    beneficiaryName = "",
    dateOfPayment = "",
    paymentReference = ""
  } = state || {};

  const actualPolicyNumber = policyNumber || "N/A";
  const hasPaymentReference = !!paymentReference;

  const prefilledMsg = encodeURIComponent(
    `Hello, I need help with policy ${actualPolicyNumber} (Ref: ${paymentReference.slice(-6) || ''})`
  );
  const whatsappLink = `https://wa.me/${whatsappNumber.replace("+", "")}/?text=${prefilledMsg}`;

  const handleDownloadPolicy = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Critical validations
      if (!paymentReference) {
        throw new Error("Payment verification required - please try again later");
      }

      if (!actualPolicyNumber || actualPolicyNumber === "N/A") {
        throw new Error("Invalid policy document ID");
      }

      const functions = getFunctions(app);
      const generatePolicyPDF = httpsCallable(functions, "generatePolicyDocument");

      // Structure data to match cloud function expectations
      const requestData = {
        paymentReference,
        policyNumber: actualPolicyNumber,
        fullName,
        dateOfBirth,
        beneficiaryName,
        coverOption: coverOption.toLowerCase(),
        tier: tier.replace("family_option_", "Tier "),
        annualAmount,
        dateOfPayment,
        email,
        phoneNumber
      };

      const response = await generatePolicyPDF(requestData);

      if (!response?.data?.pdfUrl) {
        throw new Error("Failed to generate PDF - invalid server response");
      }

      // Open in new tab with proper security attributes
      const newWindow = window.open(response.data.pdfUrl, "_blank", "noopener,noreferrer");
      if (!newWindow) {
        throw new Error("Pop-up blocked - please allow pop-ups for this site");
      }
    } catch (error) {
      setError(error.message);
      console.error("PDF Generation Error:", {
        error,
        requestData: {
          paymentReference,
          actualPolicyNumber,
          annualAmount,
          dateOfPayment
        },
        stateData: state
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="confirmation-page" style={{ 
      backgroundColor: '#f9f9f9', 
      minHeight: '100vh', 
      padding: '2rem 1rem'
    }}>
      <div className="confirmation-container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2.5rem 2rem',
        background: '#ffffff',
        borderRadius: '0.75rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      }}>
        <div className="success-header" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          margin: '0 auto 2rem',
          maxWidth: '600px',
          paddingLeft: '1.5rem'
        }}>
          <FaCheckCircle style={{
            color: '#38a169',
            fontSize: '2.5rem',
            flexShrink: 0,
            width: '1.5em',
            height: '1.5em'
          }} />
          <div className="header-text" style={{ flex: 1, textAlign: 'left' }}>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: '600',
              color: '#000',
              marginBottom: '0.5rem'
            }}>
              {hasPaymentReference ? 'Policy Confirmed' : 'Payment Processing'}
            </h1>
            <p style={{
              fontSize: '1.1rem',
              color: '#666',
              lineHeight: '1.4',
              margin: '0'
            }}>
              {hasPaymentReference 
                ? 'Your insurance coverage is now active!'
                : 'Your payment is being verified. Please wait...'}
            </p>
          </div>
        </div>

        <div className="policy-details" style={{
          maxWidth: '600px',
          margin: '0 auto 2rem',
          padding: '1.5rem',
          background: '#f9f9f9',
          borderRadius: '0.5rem',
          textAlign: 'left'
        }}>
          <div className="detail-row" style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
            padding: '0.5rem 0',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontSize: '0.95rem', color: '#666', fontWeight: '500' }}>
              Policy Number:
            </span>
            <span style={{ fontSize: '0.95rem', color: '#000', fontWeight: '600' }}>
              {actualPolicyNumber}
            </span>
          </div>

          <div className="detail-row" style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
            padding: '0.5rem 0',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontSize: '0.95rem', color: '#666', fontWeight: '500' }}>
              Policy Holder:
            </span>
            <span style={{ fontSize: '0.95rem', color: '#000', fontWeight: '600' }}>
              {fullName || 'N/A'}
            </span>
          </div>

          <div className="detail-row" style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
            padding: '0.5rem 0',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontSize: '0.95rem', color: '#666', fontWeight: '500' }}>
              Coverage Tier:
            </span>
            <span style={{ fontSize: '0.95rem', color: '#000', fontWeight: '600' }}>
              {coverOption} {tier && `- ${tier.replace(/_/g, ' ')}`}
            </span>
          </div>

          <div className="detail-row" style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
            padding: '0.5rem 0'
          }}>
            <span style={{ fontSize: '0.95rem', color: '#666', fontWeight: '500' }}>
              Annual Premium:
            </span>
            <span style={{ fontSize: '0.95rem', color: '#000', fontWeight: '600' }}>
              GH₵ {annualAmount.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="button-group" style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          marginTop: '2rem'
        }}>
          <a
            href={whatsappLink}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1rem 2rem',
              borderRadius: '0.5rem',
              fontWeight: '600',
              background: '#25D366',
              color: 'white',
              textDecoration: 'none',
              transition: 'all 0.2s ease'
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaWhatsapp /> WhatsApp Support
          </a>
          
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1rem 2rem',
              borderRadius: '0.5rem',
              fontWeight: '600',
              background: hasPaymentReference ? '#2d3748' : '#718096',
              color: 'white',
              border: 'none',
              cursor: hasPaymentReference ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease'
            }}
            onClick={handleDownloadPolicy}
            disabled={isLoading || !hasPaymentReference}
          >
            {isLoading ? (
              <span>Generating PDF...</span>
            ) : (
              <>
                <FaFileDownload /> 
                {hasPaymentReference ? 'Download Policy' : 'Awaiting Verification'}
              </>
            )}
          </button>
        </div>

        {error && (
          <div style={{
            color: '#e53e3e',
            textAlign: 'center',
            marginTop: '1rem',
            fontSize: '0.9rem',
            padding: '1rem',
            border: '1px solid #fed7d7',
            borderRadius: '0.375rem',
            backgroundColor: '#fff5f5'
          }}>
            {error}
            {!hasPaymentReference && (
              <div style={{ marginTop: '0.5rem' }}>
                Payments typically verify within 2-3 minutes. Refresh the page if status doesn't update.
              </div>
            )}
          </div>
        )}

        <p style={{
          marginTop: '2rem',
          color: '#666',
          fontSize: '0.95rem',
          lineHeight: '1.6',
          textAlign: 'center'
        }}>
          {hasPaymentReference 
            ? `Confirmation details sent to ${phoneNumber}`
            : 'Complete payment verification to receive confirmation'}
          <br />
          For urgent assistance: {contactPhone} | {support_email}
        </p>
      </div>
    </div>
  );
};

export default ConfirmationPage;